import { useState, useCallback, useRef, useMemo } from 'react';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import { ImageProps } from '../../image';

export interface SlideImageData {
  /** Image source URL - optional when media.mediaId is provided */
  src?: string;
  media?: ImageProps['media'];
  alt?: string;
}

interface UseSliderModalReturn {
  modalOpen: boolean;
  activeImageUrl: string | null;
  activeImageMedia: ImageProps['media'] | undefined;
  activeImageAlt: string | null;
  images: SlideImageData[];
  activeIndex: number;
  totalImages: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  handleSlideClick: (
    imageData: SlideImageData,
    triggerElement?: HTMLElement,
    allImages?: SlideImageData[],
    clickedIndex?: number,
  ) => void;
  handleCloseModal: () => void;
  goToImage: (index: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
}

/**
 * Custom hook for managing slider modal state and interactions.
 *
 * Handles opening/closing a modal to display enlarged images from slider slides.
 * Accepts explicit image data rather than extracting from React elements.
 * Supports full gallery navigation with thumbnails, arrows, and keyboard navigation.
 *
 * Performance: When enabled parameter is false, the hook still initializes state
 * (required by React's rules of hooks), but the state will not be used.
 * This is an acceptable trade-off for API simplicity and correctness.
 *
 * Accessibility: Automatically restores focus to the triggering element when
 * the modal closes (WCAG 2.4.3 Focus Order).
 *
 * @param {boolean} enabled - Whether modal functionality is enabled
 * @returns {UseSliderModalReturn} Modal state and handler functions
 *
 * @example
 * ```tsx
 * const { modalOpen, activeImageUrl, handleSlideClick, handleCloseModal, goToNext, goToPrevious } = useSliderModal(true);
 *
 * // Use in slide click handler with explicit image data, trigger element, and full array for navigation
 * <div onClick={(e) => handleSlideClick(imageData, e.currentTarget, allImages, index)}>
 *   <Image src={image.src} alt={image.alt} />
 * </div>
 *
 * // Render modal with navigation
 * <SliderModal
 *   isOpen={modalOpen}
 *   onClose={handleCloseModal}
 *   images={images}
 *   activeIndex={activeIndex}
 *   onNavigate={goToImage}
 *   onNext={goToNext}
 *   onPrevious={goToPrevious}
 * />
 * ```
 */
interface ModalState {
  isOpen: boolean;
  images: SlideImageData[];
  activeIndex: number;
}

const INITIAL_MODAL_STATE: ModalState = {
  isOpen: false,
  images: [],
  activeIndex: 0,
};

export const useSliderModal = (enabled = true): UseSliderModalReturn => {
  const [modalState, setModalState] = useState<ModalState>(INITIAL_MODAL_STATE);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const { translate } = useTranslation();

  // Derive active image data from state
  const activeImage = modalState.images[modalState.activeIndex];
  const activeImageUrl = activeImage?.src ?? null;
  const activeImageMedia = activeImage?.media;
  const activeImageAlt =
    activeImage?.alt || translate('common.enlarged-view', { defaultMessage: 'Enlarged view' }) || null;

  // Navigation state
  const totalImages = modalState.images.length;
  const canGoNext = modalState.activeIndex < totalImages - 1;
  const canGoPrevious = modalState.activeIndex > 0;

  const handleSlideClick = useCallback(
    (
      imageData: SlideImageData,
      triggerElement?: HTMLElement,
      allImages?: SlideImageData[],
      clickedIndex?: number,
    ) => {
      // Only open modal if enabled and valid image data provided (non-empty src or media.mediaId)
      const hasValidSource = (imageData.src && imageData.src.length > 0) || imageData.media?.mediaId;
      if (enabled && hasValidSource) {
        // Store the element that triggered the modal for focus restoration.
        // Prefer explicit triggerElement (from event.currentTarget) over document.activeElement
        // because touch interactions don't automatically focus the tapped element.
        triggerElementRef.current = triggerElement || (document.activeElement as HTMLElement);

        // Use full image array if provided, otherwise create single-image array
        const images = allImages && allImages.length > 0 ? allImages : [imageData];
        const activeIndex = clickedIndex !== undefined && clickedIndex >= 0 ? clickedIndex : 0;

        setModalState({
          isOpen: true,
          images,
          activeIndex,
        });
      }
    },
    [enabled],
  );

  const handleCloseModal = useCallback(() => {
    // Reset to initial state to free memory and prevent stale data flash on reopen
    setModalState(INITIAL_MODAL_STATE);
    // Restore focus to the element that triggered the modal
    triggerElementRef.current?.focus();
    triggerElementRef.current = null;
  }, []);

  const goToImage = useCallback((index: number) => {
    setModalState((prev) => {
      // Clamp index to valid range
      const clampedIndex = Math.max(0, Math.min(index, prev.images.length - 1));
      if (clampedIndex === prev.activeIndex) return prev;
      return { ...prev, activeIndex: clampedIndex };
    });
  }, []);

  const goToNext = useCallback(() => {
    setModalState((prev) => {
      if (prev.activeIndex >= prev.images.length - 1) return prev;
      return { ...prev, activeIndex: prev.activeIndex + 1 };
    });
  }, []);

  const goToPrevious = useCallback(() => {
    setModalState((prev) => {
      if (prev.activeIndex <= 0) return prev;
      return { ...prev, activeIndex: prev.activeIndex - 1 };
    });
  }, []);

  return useMemo(
    () => ({
      modalOpen: modalState.isOpen,
      activeImageUrl,
      activeImageMedia,
      activeImageAlt,
      images: modalState.images,
      activeIndex: modalState.activeIndex,
      totalImages,
      canGoNext,
      canGoPrevious,
      handleSlideClick,
      handleCloseModal,
      goToImage,
      goToNext,
      goToPrevious,
    }),
    [
      modalState.isOpen,
      modalState.images,
      modalState.activeIndex,
      activeImageUrl,
      activeImageMedia,
      activeImageAlt,
      totalImages,
      canGoNext,
      canGoPrevious,
      handleSlideClick,
      handleCloseModal,
      goToImage,
      goToNext,
      goToPrevious,
    ],
  );
};
